import React, { useState, useEffect } from 'react';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';
import { useConfig } from '../context/ConfigContext';
import jsPDF from 'jspdf';

const Certificate = () => {
    const { user } = useAuth();
    const { config } = useConfig();
    const [eligible, setEligible] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isDownloading, setIsDownloading] = useState(false);

    const certData = {
        imageUrl: '',
        x: 50,
        y: 75,
        fontSize: 40,
        color: '#000000',
        bypassAttendance: false,
        showSignature: true,
        signatureX: 50,
        signatureY: 85,
        signatureWidth: 40,
        signatureHeight: 20,
        ...(config?.event_letters?.certificateData || {})
    };

    useEffect(() => {
        if (certData.bypassAttendance) {
            setEligible(true);
            setIsLoading(false);
        } else {
            checkEligibility();
        }
    }, [certData.bypassAttendance]);

    const checkEligibility = async () => {
        try {
            const response = await api.get('/attendance/my-status');
            setEligible(response.data.hasAttendance);
        } catch (error) {
            console.error("Error checking status", error);
        } finally {
            setIsLoading(false);
        }
    };

    const CACHE_BUSTER = React.useMemo(() => Date.now(), []);
    const [preloadedImg, setPreloadedImg] = useState(null);

    // Preload image to make download fast
    useEffect(() => {
        if (certData.imageUrl) {
            const preload = async () => {
                try {
                    const proxyUrl = `https://api.allorigins.win/raw?url=${encodeURIComponent(certData.imageUrl)}`;
                    const img = new Image();
                    img.crossOrigin = "anonymous";
                    img.src = proxyUrl;
                    await new Promise((resolve, reject) => {
                        img.onload = resolve;
                        img.onerror = reject;
                    });
                    setPreloadedImg(img);
                } catch (error) {
                    console.error("Error preloading image", error);
                }
            };
            preload();
        }
    }, [certData.imageUrl]);

    const handleDownload = async () => {
        setIsDownloading(true);
        try {
            const pdf = new jsPDF({
                orientation: 'landscape',
                unit: 'mm',
                format: 'a4'
            });

            let img = preloadedImg;

            if (!img) {
                // Fallback if not preloaded yet
                const proxyUrl = `https://api.allorigins.win/raw?url=${encodeURIComponent(certData.imageUrl)}`;
                img = new Image();
                img.crossOrigin = "anonymous";
                img.src = proxyUrl;
                await new Promise((resolve, reject) => {
                    img.onload = resolve;
                    img.onerror = reject;
                });
            }

            const pdfWidth = pdf.internal.pageSize.getWidth();
            const imgWidth = img.naturalWidth;
            const imgHeight = img.naturalHeight;
            const ratio = imgHeight / imgWidth;
            const renderedHeight = pdfWidth * ratio;

            pdf.addImage(img, 'PNG', 0, 0, pdfWidth, renderedHeight, undefined, 'FAST');

            const fontSizePt = certData.fontSize * 1.2;
            pdf.setFontSize(fontSizePt);
            pdf.setTextColor(certData.color);
            pdf.setFont('helvetica', 'bolditalic');

            const name1 = user?.nombre_1 || user?.name || '';
            const name2 = user?.nombre_2 || '';
            const last1 = user?.apellido_1 || user?.last_name || '';
            const last2 = user?.apellido_2 || '';
            const fullName = `${name1} ${name2} ${last1} ${last2}`.replace(/\s+/g, ' ').trim().toUpperCase();

            const xMm = (certData.x / 100) * pdfWidth;
            const yMm = (certData.y / 100) * renderedHeight;

            pdf.text(fullName, xMm, yMm, { align: 'center', baseline: 'middle' });

            if (certData.showSignature && user?.signature) {
                try {
                    const sigImg = new Image();
                    sigImg.src = user.signature;
                    await new Promise((resolve, reject) => {
                        sigImg.onload = resolve;
                        sigImg.onerror = reject;
                    });
                    
                    const sigXMm = (certData.signatureX / 100) * pdfWidth;
                    const sigYMm = (certData.signatureY / 100) * renderedHeight;
                    const sigWMm = (certData.signatureWidth / 100) * pdfWidth;
                    const sigHMm = (certData.signatureHeight / 100) * renderedHeight;
                    
                    pdf.addImage(sigImg, 'PNG', sigXMm - (sigWMm / 2), sigYMm - (sigHMm / 2), sigWMm, sigHMm, undefined, 'FAST');
                } catch (sigErr) {
                    console.error("Error drawing signature", sigErr);
                }
            }

            pdf.save(`Certificado_${user?.name || name1}_${user?.last_name || last1}.pdf`);
        } catch (error) {
            console.error("Error generating PDF", error);
            alert("Error al generar el PDF.");
        } finally {
            setIsDownloading(false);
        }
    };

    if (isLoading) {
        return (
            <div className="flex flex-col items-center justify-center py-40 animate-pulse">
                <div className="w-12 h-12 border-4 border-white/10 border-t-purple-500 rounded-full animate-spin"></div>
                <p className="mt-4 text-[10px] font-black uppercase tracking-widest text-white/20">Validando credenciales</p>
            </div>
        );
    }

    if (!eligible) {
        return (
            <div className="max-w-xl mx-auto py-20 text-center animate-fadeIn">
                <div className="bg-white/10 backdrop-blur-xl p-12 rounded-[50px] border border-white/10 shadow-2xl">
                    <span className="text-6xl mb-6 block drop-shadow-lg">🎓</span>
                    <h2 className="text-2xl font-black text-white uppercase italic mb-4">Certificación no disponible</h2>
                    <p className="text-white/60 font-medium leading-relaxed">
                        Para descargar tu certificado de asistencia, es necesario haber registrado tu entrada al evento mediante el escaneo de tu código QR.
                    </p>
                    <div className="mt-8 pt-8 border-t border-white/5 italic text-[10px] text-white/20 uppercase tracking-widest">
                        Si crees que esto es un error, contacta al personal de soporte.
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="max-w-6xl mx-auto animate-fadeIn pb-20 px-4">
            <header className="mb-10 flex flex-col md:flex-row justify-between items-center gap-6 text-center md:text-left">
                <div>
                    <h2 className="text-4xl font-black text-white italic uppercase tracking-tighter">Tu Certificado</h2>
                    <p className="text-white/80 text-sm mt-1 font-medium italic">Documento oficial de asistencia</p>
                </div>
                <button
                    onClick={handleDownload}
                    disabled={isDownloading}
                    className="group relative bg-white text-[#1c0042] px-10 py-5 rounded-full font-black uppercase tracking-[0.2em] text-[10px] shadow-2xl hover:scale-105 transition-all active:scale-95 disabled:opacity-50 flex items-center gap-3 overflow-hidden"
                >
                    <div className="absolute inset-0 bg-purple-600 translate-y-full group-hover:translate-y-0 transition-transform duration-300"></div>
                    <span className="relative z-10 group-hover:text-white transition-colors">{isDownloading ? 'Ajustando Proporciones...' : 'Descargar Certificado (PDF)'}</span>
                    <span className="relative z-10 text-lg group-hover:text-white transition-colors">📄</span>
                </button>
            </header>

            <div className="relative w-full max-w-5xl mx-auto group shadow-3xl rounded-[40px] overflow-hidden bg-slate-900/20 backdrop-blur-sm p-4">
                <div
                    className="relative w-full bg-white overflow-hidden shadow-2xl"
                    style={{ minWidth: '300px' }}
                >
                    <img
                        src={`${certData.imageUrl}?cb=${CACHE_BUSTER}`}
                        alt="Certificado Base"
                        className="w-full h-auto block pointer-events-none"
                    />

                    <div
                        className="absolute flex items-center justify-center whitespace-nowrap font-black italic uppercase tracking-tighter pointer-events-none drop-shadow-md"
                        style={{
                            left: `${certData.x}%`,
                            top: `${certData.y}%`,
                            fontSize: `calc(1.8vw * ${certData.fontSize / 40})`,
                            color: certData.color,
                            transform: 'translate(-50%, -50%)',
                            lineHeight: '1'
                        }}
                    >
                        {`${user?.nombre_1 || user?.name || ''} ${user?.nombre_2 || ''} ${user?.apellido_1 || user?.last_name || ''} ${user?.apellido_2 || ''}`.replace(/\s+/g, ' ').trim().toUpperCase()}
                    </div>
                </div>

                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center pointer-events-none rounded-[40px]">
                    <p className="text-white text-[10px] font-black uppercase tracking-[0.4em] italic bg-white/10 backdrop-blur-xl px-10 py-4 rounded-full border border-white/20">
                        Vista Previa del Certificado
                    </p>
                </div>
            </div>

            <div className="mt-10 text-center space-y-4 max-w-2xl mx-auto">
                <p className="text-[10px] text-white/70 uppercase font-black tracking-widest leading-relaxed">
                    Certificado generado con precisión de aspecto ({certData.x}%, {certData.y}%).
                </p>
            </div>
        </div>
    );
};

export default Certificate;
